/**
 * Session token claim reader. Decodes the JWT payload WITHOUT verifying the
 * signature: the signing secret lives only in measuring_api, never in the bundle.
 *
 * Called by: components/App.js, components/EmailVerify.js, contexts/Auth.context.js
 * Gotchas: the result is NOT proof of authenticity or freshness. Only use it to read
 * routing claims (userId, adminId, device). The server is the only authority; a rejected
 * token arrives as a 401 "failed-token" response (see UserService.getUserData).
 */

/**
 * @param {string} segment - base64url string.
 * @returns {string} UTF-8 decoded text.
 */
const decodeBase64Url = (segment) => {
  const base64 = segment.replace(/-/g, "+").replace(/_/g, "/");
  const padded = base64 + "=".repeat((4 - (base64.length % 4)) % 4);
  const binary = atob(padded);
  const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0));
  return new TextDecoder().decode(bytes);
};

/**
 * Reads the claims of a JWT without verifying its signature or expiration.
 * @param {string} token - Encoded JWT.
 * @returns {Promise<object>} Payload claims; rejects when the token is malformed.
 */
export const decodeToken = function (token) {
  return new Promise((resolve, reject) => {
    try {
      const parts = typeof token === "string" ? token.split(".") : [];
      if (parts.length !== 3) throw new Error("Malformed token");
      const payload = JSON.parse(decodeBase64Url(parts[1]));
      if (payload === null || typeof payload !== "object") {
        throw new Error("Malformed token payload");
      }
      resolve(payload);
    } catch (err) {
      reject(err);
    }
  });
};
